import React, { useState, useEffect } from "react";
import "./ContactForm.css";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

const ContactForm = ({ showTitle = true }) => {

  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    captchaInput: ""
  });

  const [captcha, setCaptcha] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  /* -------------------------------------------
  Marathi / Hindi digit conversion
  ------------------------------------------- */

  const normalizeDigits = (str) => {

    if (!str) return str;

    const map = {
      "०":"0","१":"1","२":"2","३":"3","४":"4",
      "५":"5","६":"6","७":"7","८":"8","९":"9"
    };

    return str.replace(/[०१२३४५६७८९]/g, d => map[d]);
  };

  /* -------------------------------------------
  CAPTCHA
  ------------------------------------------- */

  const generateCaptcha = () => {
    const newCaptcha = Math.floor(1000 + Math.random() * 9000).toString();
    setCaptcha(newCaptcha);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  /* -------------------------------------------
  INPUT HANDLER
  ------------------------------------------- */

  const handleChange = (e) => {

    const { name, value } = e.target;

    /* Name supports Hindi Marathi English */

    if (name === "name") {
      if (!/^[\p{L}\p{M}\s.'-]*$/u.test(value)) return;
      setFormData({ ...formData, [name]: value });
      return;
    }

    /* Phone supports Marathi digits */

    if (name === "phone") {

      const normalized = normalizeDigits(value);

      if (!/^[0-9]*$/.test(normalized)) return;

      setFormData({ ...formData, phone: normalized });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  /* -------------------------------------------
  VALIDATION
  ------------------------------------------- */

  const validate = () => {

    let newErrors = {};

    const cleanData = {
      name: formData.name.trim(),
      phone: normalizeDigits(formData.phone.trim()),
      email: formData.email.trim(),
      message: formData.message.trim(),
      captchaInput: formData.captchaInput.trim()
    };

    if (!cleanData.name)
      newErrors.name = t("err_name_req");

    else if (cleanData.name.length < 2)
      newErrors.name = t("err_name_short");

    if (!cleanData.phone)
      newErrors.phone = t("err_phone_req");

    else if (!/^[0-9]{10}$/.test(cleanData.phone))
      newErrors.phone = t("err_phone_invalid");

    if (!cleanData.email)
      newErrors.email = t("err_email_req");

    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanData.email))
      newErrors.email = t("err_email_invalid");

    if (!cleanData.message)
      newErrors.message = t("err_msg_req");

    if (!cleanData.captchaInput)
      newErrors.captcha = t("err_captcha_req");

    else if (cleanData.captchaInput !== captcha)
      newErrors.captcha = t("err_captcha_match");

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* -------------------------------------------
  SUBMIT
  ------------------------------------------- */

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (submitting) return;

    if (validate()) {

      setSubmitting(true);

      try {

        const payload = {
          ...formData,
          phone: normalizeDigits(formData.phone)
        };

        const response = await fetch(`${API_URL}/api/contact`, {

          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },

          body: JSON.stringify(payload)

        });

        const data = await response.json();

        Swal.fire({

          icon: "success",
          title: t("msg_success_title", "Message Sent!"),
          text: data.message || t("msg_success"),
          confirmButtonText: "OK",
          confirmButtonColor: "#4b3bbd",
          background: "#ffffff",
          iconColor: "#28a745",
          timer: 3500,
          timerProgressBar: true

        });

        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
          captchaInput: ""
        });

        setErrors({});
        generateCaptcha();

      } catch (error) {

        Swal.fire({
          icon: "error",
          title: t("msg_error_title", "Server Error"),
          text: t("msg_server_err"),
          confirmButtonText: "Try Again",
          confirmButtonColor: "#d33"
        });

      }

      setSubmitting(false);

    }
  };

  /* -------------------------------------------
  UI
  ------------------------------------------- */

  return (

    <div className="contact_form_card">

      {showTitle && <h3>{t("form_title")}</h3>}

      <form onSubmit={handleSubmit} noValidate>

        <input
          type="text"
          name="name"
          placeholder={t("form_name_placeholder")}
          value={formData.name}
          onChange={handleChange}
          className={errors.name ? "input-error" : ""}
        />

        {errors.name && <small className="error">{errors.name}</small>}

        <input
          type="tel"
          name="phone"
          placeholder={t("form_phone_placeholder")}
          maxLength="10"
          value={formData.phone}
          onChange={handleChange}
          className={errors.phone ? "input-error" : ""}
        />

        {errors.phone && <small className="error">{errors.phone}</small>}

        <input
          type="email"
          name="email"
          placeholder={t("form_email_placeholder")}
          value={formData.email}
          onChange={handleChange}
          className={errors.email ? "input-error" : ""}
        />

        {errors.email && <small className="error">{errors.email}</small>}

        <textarea
          name="message"
          placeholder={t("form_msg_placeholder")}
          rows="4"
          value={formData.message}
          onChange={handleChange}
          className={errors.message ? "input-error" : ""}
        />

        {errors.message && <small className="error">{errors.message}</small>}

        <div className="captcha_container">

          <div className="captcha_box">
            {captcha}
          </div>

          <input
            type="text"
            name="captchaInput"
            placeholder={t("form_captcha_placeholder")}
            value={formData.captchaInput}
            onChange={handleChange}
            className={errors.captcha ? "input-error" : ""}
          />

        </div>

        {errors.captcha && <small className="error">{errors.captcha}</small>}

        <button type="submit" disabled={submitting}>
          {t("form_submit_btn")}
        </button>

      </form>

    </div>

  );
};

export default ContactForm;